import * as ReactDOM from 'react-dom/client'
import './styles.css'
import App from './app/app'
import { Provider } from 'react-redux'
import { PersistGate } from '@app-store'
import { store, persist } from './app/utils/agent.store'
import { Helmet } from 'react-helmet'
import { environment } from './app/utils/environment'
import { clearForceMsalPromptNextLogin } from '@app-store'
import { broadcastLoginSuccess } from '@ecrm-session'
import { msalInstance } from './app/utils/msalConfig'
import {
  clearSessionPostLogoutReturnPath,
  getSessionPostLogoutReturnPath,
  isSafeReturnPath,
} from './app/utils/postLogoutReturnPath'
import { setMsalAccessTokenFromRedirect } from '@ecrm-msal'
import { EventType, AuthenticationResult } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'

let didResumeMsalThisBootstrap = false

/**
 * Broadcast first so other tabs on /logout can read their own sessionStorage return path before this tab clears.
 * Return path is per-tab sessionStorage (not Redux), so each tab resumes to the URL it had when logging out.
 */
function resumeAfterMsalLogin() {
  if (didResumeMsalThisBootstrap) return
  didResumeMsalThisBootstrap = true
  const path = getSessionPostLogoutReturnPath()
  const safe = path && isSafeReturnPath(path) ? path : null
  broadcastLoginSuccess()
  clearSessionPostLogoutReturnPath()
  store.dispatch(clearForceMsalPromptNextLogin())
  if (safe) {
    window.location.replace(`${window.location.origin}${safe}`)
  }
}

async function bootstrap() {
  try {
    //  Required in MSAL v4+
    await msalInstance.initialize()

    // Set active account after login
    msalInstance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult
        msalInstance.setActiveAccount(payload.account)
        resumeAfterMsalLogin()
      }
    })

    // Handle redirect response
    const redirectResult = await msalInstance.handleRedirectPromise()

    if (redirectResult?.accessToken) {
      setMsalAccessTokenFromRedirect(redirectResult.accessToken)
    }

    if (redirectResult?.account) {
      msalInstance.setActiveAccount(redirectResult.account)
      resumeAfterMsalLogin()
    } else {
      const activeAccount = msalInstance.getActiveAccount()
      const accounts = msalInstance.getAllAccounts()

      if (!activeAccount && accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0])
      }
    }
  } catch (e) {
    sessionStorage.setItem(
      'msal_init_error',
      e instanceof Error ? e.message : 'MSAL initialization failed'
    )
  }

  const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
  )

  root.render(
    // <StrictMode>
    <MsalProvider instance={msalInstance}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persist}>
          <Helmet>
            {environment.pendoEnable && (
              <script>
                {`(function(apiKey){
          console.log('Pendo Env key',apiKey);
    (function(p,e,n,d,o){var v,w,x,y,z;o=p[d]=p[d]||{};o._q=o._q||[];
    v=['initialize','identify','updateOptions','pageLoad','track'];for(w=0,x=v.length;w<x;++w)(function(m){
        o[m]=o[m]||function(){o._q[m===v[0]?'unshift':'push']([m].concat([].slice.call(arguments,0)));};})(v[w]);
        y=e.createElement(n);y.async=!0;y.src='https://cdn.pendo.io/agent/static/'+apiKey+'/pendo.js';
        z=e.getElementsByTagName(n)[0];z.parentNode.insertBefore(y,z);})(window,document,'script','pendo');
})('${environment.pendoKey}');`}
              </script>
            )}
          </Helmet>
          <App />
        </PersistGate>
      </Provider>
    </MsalProvider>
    // </StrictMode>
  )

  // Required: enhancement work in progress
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker
        .register('/service-worker.js')
        .then(() => console.log('SW registered'))
        .catch((err) => console.error('SW failed:', err))
    })
  }
}
void bootstrap()
